import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Search,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Filter,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { usePeriodeStore } from "@/hooks/use-periode";
import { toast } from "sonner";
import type { DosenRemun } from "@/pages/common/remun-dosen/api/get-dosen-remun";
import { fetchDosenRemun } from "@/pages/common/remun-dosen/api/get-dosen-remun";
import { putValidatorDetail } from "@/pages/common/remun-dosen/api/put-validator-detail";
import { useSearchParams } from "react-router";
import { apiClient } from "@/lib/api-client";
import { FilterCombobox } from "@/components/ui/filter-combobox";

// Grid View Component for Dosen
function DosenCard({
  dosen,
  year,
  semester,
  role,
}: {
  dosen: DosenRemun;
  year: number | string;
  semester: number | string;
  role: string;
}) {
  const queryClient = useQueryClient();

  const validatorMutation = useMutation({
    mutationFn: putValidatorDetail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dosen-remun"] });
      toast.success("Status validasi berhasil diperbarui.");
    },
    onError: (error: any) => {
      toast.error(error?.message || "Gagal memperbarui status validasi");
    },
  });

  const handleToggleValidasi = (month: string, is_validator: boolean) => {
    validatorMutation.mutate({
      year,
      semester,
      niu: dosen.niu,
      month,
      is_validator,
      role: role || "",
    });
  };

  const months = Object.keys(dosen.data_per_bulan || {}).sort();
  // Attempt to get fakultas from the latest month's jabatan_aktif
  const latestMonthData =
    months.length > 0 ? dosen.data_per_bulan[months[months.length - 1]] : null;
  const activeJabatan = (latestMonthData as any)?.jabatan_aktif?.[0];
  const fakultas =
    dosen.unit?.fakultas ||
    dosen.Unit?.Fakultas ||
    activeJabatan?.uk_nama ||
    "-";

  return (
    <Card className="shadow-md border-2 border-slate-200/80 bg-white mb-8 rounded-xl relative">
      <CardHeader className="bg-slate-50 border-b border-slate-100 py-4 px-6 rounded-t-xl">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div>
            <CardTitle className="text-lg text-slate-800">
              {dosen.nama || "-"}
            </CardTitle>
            <CardDescription className="text-slate-500 mt-1">
              NIP: {dosen.NIP || dosen.nip || "-"} • {fakultas}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-6 bg-slate-50/50">
        {months.length === 0 ? (
          <div className="text-center py-8 text-slate-500 italic bg-white rounded-lg border border-slate-200">
            Tidak ada rincian bulan.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-3 lg:gap-4">
            {months.map((month) => {
              const data = dosen.data_per_bulan[month];
              const currentRoleKey = role.replace(/\s+/g, "_").toLowerCase();
              const currentRoleValidation = data.validator_details?.find(
                (v: any) =>
                  v.level.replace(/\s+/g, "_").toLowerCase() === currentRoleKey,
              );
              const myValidationState = currentRoleValidation?.validator_name
                ? currentRoleValidation.is_valid_taken
                  ? "valid"
                  : "invalid"
                : "unvalidated";

              // Parse month name
              const monthNum = parseInt(month.split("_")[1], 10);
              const monthName = new Date(0, monthNum - 1).toLocaleString(
                "id-ID",
                { month: "long" },
              );

              return (
                <div
                  key={`${dosen.niu}-${month}`}
                  className="border border-slate-200 rounded-xl p-3 lg:p-4 bg-white shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] flex flex-col justify-between hover:border-primary/30 transition-colors"
                >
                  <div>
                    <div
                      className="text-[10px] text-slate-500 font-semibold text-center tracking-wide mb-1 truncate"
                      title={dosen.nama || ""}
                    >
                      {dosen.nama}
                    </div>
                    <h5 className="font-bold text-slate-800 mb-3 border-b border-slate-100 pb-2 text-center text-[13px] lg:text-sm">
                      {monthName}
                    </h5>
                    <div className="space-y-2 text-[10px] lg:text-xs mb-5">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-slate-500 font-medium">
                          Pegawai:
                        </span>
                        <span className="font-semibold text-slate-700">
                          {data.status_pegawai || "-"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-slate-500 font-medium">
                          Dosen:
                        </span>
                        <span className="font-semibold text-slate-700">
                          {data.status_sekarang || "-"}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5 mt-2">
                        <span className="text-slate-500 font-medium mt-0.5">
                          Fungsional:
                        </span>
                        <span className="font-semibold text-slate-700 leading-tight">
                          {data.jabatan_fungsional &&
                          data.jabatan_fungsional !== "-" ? (
                            <>
                              {data.jabatan_fungsional}{" "}
                              <span className="text-slate-400 text-[9px] lg:text-[10px] block font-normal mt-0.5">
                                Grade. {data.grade_fungsional || 0}
                              </span>
                            </>
                          ) : (
                            "-"
                          )}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-slate-500 font-medium mt-0.5">
                          Struktural:
                        </span>
                        <span className="font-semibold text-slate-700 leading-tight">
                          {data.jabatan_struktural &&
                          data.jabatan_struktural !== "-" ? (
                            <>
                              {data.jabatan_struktural}{" "}
                              <span className="text-slate-400 text-[9px] lg:text-[10px] block font-normal mt-0.5">
                                Gr. {data.grade_struktural || 0}
                              </span>
                            </>
                          ) : (
                            "-"
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-3 border-t border-slate-100 pt-2">
                        <span className="text-slate-500 font-medium">
                          Golongan:
                        </span>
                        <span className="font-bold text-slate-700">
                          {data.golongan || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    {/* List Validator Details */}
                    <div className="mb-3 flex flex-col gap-1.5 text-xs">
                      {["dosen", "admin_htl", "admin_kepegawaian"].map(
                        (level: string, vIdx: number) => {
                          const v = data.validator_details?.find(
                            (vd: any) => vd.level === level,
                          ) || {
                            level,
                            is_valid_taken: false,
                            validator_name: "",
                          };

                          const isUnvalidated = !v.validator_name;
                          const isValid = v.is_valid_taken === true;
                          const isInvalid =
                            !isUnvalidated && v.is_valid_taken === false;

                          const levelName =
                            v.level.toLowerCase() === "dosen"
                              ? "DOSEN"
                              : v.level.toLowerCase() === "admin super" ||
                                  v.level.toLowerCase() === "admin_super"
                                ? "SUPER"
                                : v.level
                                    .replace(/^admin[\s_]+/, "")
                                    .toUpperCase();
                          return (
                            <div
                              key={vIdx}
                              className="flex items-center justify-between bg-white px-2 py-1.5 rounded border border-slate-100 shadow-sm"
                            >
                              <span className="font-medium text-slate-600">
                                {levelName}
                              </span>
                              {isValid ? (
                                <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
                                  <CheckCircle2 className="w-3 h-3" />{" "}
                                  {v.validator_name || "Valid"}
                                </span>
                              ) : isInvalid ? (
                                <span className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-100">
                                  <XCircle className="w-3 h-3" />{" "}
                                  {v.validator_name || "Tidak Valid"}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-200">
                                  Belum Divalidasi
                                </span>
                              )}
                            </div>
                          );
                        },
                      )}
                    </div>
                    <div className="flex gap-2 w-full mt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className={`flex-1 text-[10px] h-8 font-semibold transition-all ${
                          myValidationState === "invalid"
                            ? "bg-red-500 text-white border-red-500 hover:bg-red-600 hover:text-white"
                            : "text-red-600 border-red-200 bg-red-50 hover:bg-red-100 hover:text-red-700"
                        }`}
                        onClick={() => handleToggleValidasi(month, false)}
                        disabled={validatorMutation.isPending}
                      >
                        {validatorMutation.isPending &&
                        myValidationState === "invalid" ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 mr-1" />
                        )}
                        Tidak Valid
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className={`flex-1 text-[10px] h-8 font-semibold transition-all ${
                          myValidationState === "valid"
                            ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600 hover:text-white"
                            : "text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:text-emerald-700"
                        }`}
                        onClick={() => handleToggleValidasi(month, true)}
                        disabled={validatorMutation.isPending}
                      >
                        {validatorMutation.isPending &&
                        myValidationState === "valid" ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                        ) : myValidationState === "unvalidated" ? (
                          <span className="relative flex h-2 w-2 mr-1">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                          </span>
                        ) : (
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        )}
                        Valid
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ValidasiJabatanPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = searchParams.get("page");
  const limitParam = searchParams.get("limit");
  const searchParam = searchParams.get("search") || "";
  const statusPegawaiParam = searchParams.get("id_status_pegawai") || "all";
  const statusSekarangParam = searchParams.get("id_status_sekarang") || "all";
  const fktKodeParam = searchParams.get("fkt_kode") || "all";
  const prdKodeParam = searchParams.get("prd_kode") || "all";
  const validStatusParam = searchParams.get("valid_status") || "all";

  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const limit = limitParam ? parseInt(limitParam, 10) : 5;

  const [filterNama, setFilterNama] = useState(searchParam);
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam);

  const [jumpPage, setJumpPage] = useState(page.toString());
  useEffect(() => {
    setJumpPage(page.toString());
  }, [page]);

  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester, periodeId } = getYearAndSemester();

  const updateParams = (updates: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  // Sinkronisasi jika URL berubah (misal tombol Back dari browser)
  useEffect(() => {
    setFilterNama(searchParam);
    setDebouncedSearch(searchParam);
  }, [searchParam]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filterNama);
      if (filterNama !== searchParam) {
        updateParams({ search: filterNama, page: "1" });
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [filterNama, searchParam]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "dosen-remun",
      {
        year,
        semester,
        periodeID: periodeId,
        page,
        limit,
        search: debouncedSearch,
        id_status_pegawai:
          statusPegawaiParam !== "all" ? statusPegawaiParam : undefined,
        id_status_sekarang:
          statusSekarangParam !== "all" ? statusSekarangParam : undefined,
        fkt_kode: fktKodeParam !== "all" ? fktKodeParam : undefined,
        prd_kode: prdKodeParam !== "all" ? prdKodeParam : undefined,
        valid_status: validStatusParam !== "all" ? validStatusParam : undefined,
      },
    ],
    queryFn: fetchDosenRemun,
    enabled: !!year && !!semester && !!periodeId,
  });

  const { data: fakultasData, isLoading: isLoadingFakultas } = useQuery({
    queryKey: ["master-fakultas-all"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(
        `${baseUrl}/api/v1/master-fakultas?page=1&limit=1000`,
      );
    },
  });

  const { data: jurusanData, isLoading: isLoadingJurusan } = useQuery({
    queryKey: ["master-jurusan-all"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(
        `${baseUrl}/api/v1/master-jurusan?page=1&limit=1000`,
      );
    },
  });

  const filteredJurusan = useMemo(() => {
    const allJurusan = jurusanData?.datas || [];
    if (fktKodeParam === "all") return allJurusan;
    return allJurusan.filter((j: any) => j.kode_fakultas === fktKodeParam);
  }, [jurusanData, fktKodeParam]);

  const datas = useMemo(() => {
    return data?.datas || [];
  }, [data]);

  const pagination = data?.pagination;
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.pages || 1;
  const isNext = data?.is_next || page < totalPages;

  return (
    <div className="container mx-auto py-8">
      <Card className="shadow-xl border-slate-200/60 overflow-hidden bg-white/50 backdrop-blur-xl rounded-xl">
        <CardHeader className="p-6 sm:p-8 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl font-bold flex items-center gap-2 text-slate-900">
                <ShieldCheck className="w-6 h-6 text-primary/80" />
                Validasi Jabatan Dosen
              </CardTitle>
              <CardDescription className="text-slate-500 mt-1.5 text-base">
                Verifikasi dan validasi kesesuaian jabatan fungsional,
                struktural, dan golongan per bulan.
              </CardDescription>
            </div>
            {(isLoading || isFetching) && (
              <div className="flex items-center text-sm text-primary bg-primary/10 px-4 py-2 rounded-full font-medium">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Memuat data...
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6 sm:p-8">
          <div className="mb-8 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Filter className="w-4 h-4" />
                Filter Data
              </div>
              {(filterNama !== "" ||
                statusPegawaiParam !== "all" ||
                statusSekarangParam !== "all" ||
                fktKodeParam !== "all" ||
                prdKodeParam !== "all" ||
                validStatusParam !== "all") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterNama("");
                    setDebouncedSearch("");
                    updateParams({
                      search: "",
                      id_status_pegawai: "",
                      id_status_sekarang: "",
                      fkt_kode: "",
                      prd_kode: "",
                      valid_status: "",
                      page: "1",
                    });
                  }}
                  className="h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" />
                  Reset Filter
                </Button>
              )}
            </div>

            <div
              className={`flex flex-col gap-4 p-4 rounded-xl transition-all duration-200 border ${
                filterNama !== "" ||
                statusPegawaiParam !== "all" ||
                statusSekarangParam !== "all" ||
                fktKodeParam !== "all" ||
                prdKodeParam !== "all" ||
                validStatusParam !== "all"
                  ? "bg-primary/5 border-primary/20 shadow-sm"
                  : "bg-slate-50/50 border-slate-100"
              }`}
            >
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-1/3">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Pencarian Dosen
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Ketik nama atau NIP dosen..."
                      value={filterNama}
                      onChange={(e) => setFilterNama(e.target.value)}
                      className="w-full bg-white pl-9 border-slate-200 focus-visible:ring-primary shadow-sm rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div className="w-full md:w-1/3">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Status Pegawai
                  </label>
                  <FilterCombobox
                    value={statusPegawaiParam}
                    onValueChange={(val) =>
                      updateParams({
                        id_status_pegawai: val === "all" ? "" : val,
                        page: "1",
                      })
                    }
                    options={[
                      { label: "PNS", value: "1" },
                      { label: "CPNS", value: "4" },
                      { label: "PPPK", value: "7" },
                    ]}
                    placeholder="Semua Status Pegawai"
                    searchPlaceholder="Cari status pegawai..."
                    emptyText="Status tidak ditemukan."
                  />
                </div>

                <div className="w-full md:w-1/3">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Status Sekarang
                  </label>
                  <FilterCombobox
                    value={statusSekarangParam}
                    onValueChange={(val) =>
                      updateParams({
                        id_status_sekarang: val === "all" ? "" : val,
                        page: "1",
                      })
                    }
                    options={[
                      { label: "Tugas Belajar", value: "1" },
                      { label: "Aktif", value: "2" },
                      { label: "Ijin Belajar", value: "3" },
                    ]}
                    placeholder="Semua Status Sekarang"
                    searchPlaceholder="Cari status sekarang..."
                    emptyText="Status tidak ditemukan."
                  />
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-1/3">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Fakultas
                  </label>
                  <FilterCombobox
                    value={fktKodeParam}
                    onValueChange={(val) =>
                      updateParams({
                        fkt_kode: val === "all" ? "" : val,
                        prd_kode: "", // Reset prodi if fakultas changes
                        page: "1",
                      })
                    }
                    options={(fakultasData?.datas || []).map((fkt: any) => ({
                      label: fkt.nama_fakultas,
                      value: fkt.kode_fakultas,
                    }))}
                    placeholder={
                      isLoadingFakultas ? "Memuat..." : "Semua Fakultas"
                    }
                    searchPlaceholder="Cari fakultas..."
                    emptyText="Fakultas tidak ditemukan."
                    disabled={isLoadingFakultas}
                  />
                </div>

                <div className="w-full md:w-1/3">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Jurusan/Prodi
                  </label>
                  <FilterCombobox
                    value={prdKodeParam}
                    onValueChange={(val) =>
                      updateParams({
                        prd_kode: val === "all" ? "" : val,
                        page: "1",
                      })
                    }
                    options={filteredJurusan.map((jur: any) => ({
                      label: jur.nama_jurusan,
                      value: jur.master_jurusan_id,
                    }))}
                    placeholder={
                      isLoadingJurusan ? "Memuat..." : "Semua Jurusan/Prodi"
                    }
                    searchPlaceholder="Cari jurusan/prodi..."
                    emptyText="Jurusan/Prodi tidak ditemukan."
                    disabled={isLoadingJurusan}
                  />
                </div>

                <div className="w-full md:w-1/3">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Status Validasi (
                    {(() => {
                      if (!user?.role) return "Jabatan";
                      const r = user.role.toLowerCase().replace(/^admin_/, "");
                      return r.charAt(0).toUpperCase() + r.slice(1);
                    })()}
                    )
                  </label>
                  <FilterCombobox
                    value={validStatusParam}
                    onValueChange={(val) =>
                      updateParams({
                        valid_status: val === "all" ? "" : val,
                        page: "1",
                      })
                    }
                    options={[
                      { label: "Sudah Divalidasi", value: "valid" },
                      { label: "Belum Divalidasi", value: "invalid" },
                    ]}
                    placeholder="Semua Status Validasi"
                    searchPlaceholder="Cari status validasi..."
                    emptyText="Status tidak ditemukan."
                  />
                </div>
              </div>
            </div>
          </div>

          {isError ? (
            <div className="flex h-64 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600 font-medium">
              Terjadi kesalahan saat memuat data.
            </div>
          ) : (
            <div className="relative">
              {datas.length === 0 && !isLoading && !isFetching ? (
                <div className="text-center py-12 text-slate-500 border border-slate-200 border-dashed rounded-xl bg-slate-50/50">
                  Data dosen tidak ditemukan.
                </div>
              ) : (
                <div className="space-y-6">
                  {datas.map((dosen: DosenRemun) => (
                    <DosenCard
                      key={dosen.niu}
                      dosen={dosen}
                      year={year}
                      semester={semester}
                      role={user?.role || ""}
                    />
                  ))}
                </div>
              )}

              {/* Pagination Controls */}
              <div className="flex flex-col sm:flex-row items-center justify-between mt-8 py-4 border-t border-slate-200 gap-4">
                <div className="flex items-center gap-4 text-sm text-slate-500 font-medium">
                  <div>Total: {totalItems} Data</div>
                  <div className="flex items-center gap-2">
                    <span>Tampilkan:</span>
                    <Select
                      value={limit.toString()}
                      onValueChange={(val) =>
                        updateParams({ limit: val, page: "1" })
                      }
                    >
                      <SelectTrigger className="h-8 w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[5, 10, 20, 50].map((sz) => (
                          <SelectItem key={sz} value={sz.toString()}>
                            {sz}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <div className="text-sm text-slate-500 font-medium mr-4 flex items-center gap-2">
                    <span>Halaman</span>
                    <Input
                      className="w-14 h-8 text-center px-1"
                      value={jumpPage}
                      onChange={(e) => setJumpPage(e.target.value)}
                      onBlur={() => {
                        let val = parseInt(jumpPage, 10);
                        if (!isNaN(val) && val > 0 && val <= totalPages) {
                          updateParams({ page: val.toString() });
                        } else {
                          setJumpPage(page.toString());
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          let val = parseInt(jumpPage, 10);
                          if (!isNaN(val) && val > 0 && val <= totalPages) {
                            updateParams({ page: val.toString() });
                          } else {
                            setJumpPage(page.toString());
                          }
                        }
                      }}
                    />
                    <span>dari {totalPages}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      updateParams({ page: (page - 1).toString() })
                    }
                    disabled={page === 1}
                    className="bg-white shadow-sm"
                  >
                    Sebelumnya
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      updateParams({ page: (page + 1).toString() })
                    }
                    disabled={!isNext}
                    className="bg-white shadow-sm"
                  >
                    Selanjutnya
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
